<script setup>
//modeled on https://www.w3.org/WAI/ARIA/apg/patterns/tabs/examples/tabs-automatic/
import { onMounted, ref, useId, useSlots } from "vue";
import XScroll from "../_x-scroll/x-scroll-util";

const props = defineProps({
  headingId: {
    type: String,
    required: true,
  },
  tabs: {
    type: Array,
    required: true,
  },
  theme: {
    type: String,
    default: "default", //default | buttons
  },
});

const activeTab = ref(0);

const idModifier = useId();
const slots = useSlots();

//Lifecycle
onMounted(() => {
  XScroll();
});

//Methods
function idify(id) {
  return `${idModifier}__${id}`;
}
function setActiveTab(index) {
  if (index >= props.tabs.length) index = 0;
  if (index < 0) index = props.tabs.length - 1;
  activeTab.value = index;

  //set button focus
  const button = document.getElementById(idify("tab-" + index));
  button.focus();
}
</script>

<template>
  <div class="tabs">
    <div :class="{ xScroll: props.theme === 'buttons' }">
      <div
        role="tablist"
        class="tabs__tabList"
        :class="[
          props.theme === 'buttons'
            ? 'tabs__tabList--buttons xScroll__items'
            : 'tabs__tabList--tabs',
        ]"
        :aria-labelledby="headingId"
      >
        <button
          v-for="(tab, index) in tabs"
          :id="idify('tab-' + index)"
          class="tabs__tab"
          :class="[
            { 'tabs__tab--active': index === activeTab },
            props.theme === 'buttons'
              ? 'tabs__tab--buttons'
              : 'tabs__tab--tabs',
          ]"
          v-on:click="activeTab = index"
          v-on:keydown.right="setActiveTab(index + 1)"
          v-on:keydown.left="setActiveTab(index - 1)"
          v-on:keydown.home="setActiveTab(0)"
          v-on:keydown.end="setActiveTab(tabs.length - 1)"
          type="button"
          role="tab"
          :tabindex="index === activeTab ? null : -1"
          :aria-selected="index === activeTab"
          :aria-controls="idify('tabpanel-' + index)"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>
    <div
      v-for="(tab, index) in tabs"
      :id="idify('tabpanel-' + index)"
      class="tabs__tabPanel"
      :class="{
        'tabs__tabPanel--closed': index !== activeTab,
      }"
      role="tabpanel"
      tabindex="0"
      :aria-labelledby="idify('tab-' + index)"
    >
      <div class="tabs__tabPanelContent">
        <slot
          :name="'tabPanel-' + index"
          v-if="'tabPanel-' + index in slots"
        ></slot>
        <div v-else v-html="tab.content"></div>
      </div>
      <figure class="tabs__tabPanelFigure" v-if="tab.image">
        <img
          :src="tab.image.src"
          :width="tab.image.width"
          :height="tab.image.height"
          :srcset="tab.image.srcset"
          class="tabs__tabPanelImg"
          :class="tab.image.class"
          :alt="tab.image.altText"
        />
      </figure>
    </div>
  </div>
</template>

<style lang="scss">
@use "../_layout/mixins";
.tabs__tabPanelContent {
  a:not([class]) {
    @include mixins.plainLinkInverse();
  }
}
</style>
<style scoped lang="scss">
@use "../_layout/mixins";
@use "../_x-scroll/x-scroll.scss";
.tabs__tabList--buttons {
  display: flex;
  gap: 0.625em;
  margin-block-end: 1em;
}
.tabs__tab {
  @include mixins.blockFocus();
}
.tabs__tab--tabs {
  padding: 0.8em 1.3em;
  font-weight: var(--bold-weight);
  color: var(--blue--ultraDark);
  border: 2px solid var(--blue--ultraDark);
  background-color: var(--white);

  & + & {
    border-inline-start: 0;
  }

  &:hover,
  &:focus {
    &:not(.tabs__tab--active) {
      background-color: var(--accent);
      cursor: pointer;
    }
  }

  &.tabs__tab--active {
    color: var(--accent);
    background-color: var(--blue--ultraDark);
  }
}
.tabs__tab--buttons {
  @include mixins.buttonSmall();
  @include mixins.button();
  @include mixins.buttonSecondary();

  &.tabs__tab--active {
    background-color: var(--accent);
  }
}
.tabs__tabPanel {
  display: flex;
  flex-wrap: wrap;
  @include mixins.blockFocus();
}
.tabs__tabPanel--closed {
  display: none;
}
.tabs__tabPanelContent {
  padding: 1em;
  background-color: var(--blue--ultraDark);
  color: var(--white);
  flex: 1 1 var(--tabPanelContentFlex, 50%);
}
.tabs__tabPanelFigure {
  flex: var(--tabPanelFigureFlex, 25%);
  order: -1;
}
.tabs__tabPanelImg {
  order: -1;
  flex: 1;
  min-width: var(--tabPanelImgMinWidth, 300px);
  width: 100%;
  height: 100%;
  align-self: start;
  object-fit: cover;
}
</style>
